<template>
  <component
    v-bind="attrs"
    :is="linkTag"
    :to="linkTo"
    :[hrefAttribute]="linkHref"
    :type="linkButtonType"
    :title="title"
    :target="target"
    :aria-disabled="disabled ? 'true' : undefined"
    :disabled="disabled"
    :data-id="id"
    class="link"
    :class="linkClasses"
    :aria-label="ariaLabel || t('accessibility.general.goToUrl', { urlName: linkTo || linkHref }) || id"
    :tabindex="disabled ? -1 : 0"
    :role="linkRoleType"
    :style="{ color: tokenColor ? `var(--${tokenColor})` : undefined }"
    @click="handleClick"
    @mousedown.middle="handleMiddleClick"
    @keydown.enter="onKeydownEnter"
    @keydown.space.prevent="onKeydownEnter"
  >
    <span
      class="link-text"
      :class="{
        'link-text--pre': hasPreArrow,
        'link-text--post': hasPostArrow,
      }"
    >
      <slot />
    </span>
    <fn-icon
      v-if="arrowType"
      :icon="arrowType"
    />
  </component>
</template>

<script setup lang="ts">
import type { RouteLocation } from 'vue-router';

export type LinkEmits = {
  (name: 'click', event: PointerEvent): void;
  (name: 'priorityClick', event: PointerEvent): void;
};

export type LinkProps = {
  href?: string;
  to?: string | RouteLocation;
  target?: string;
  disabled?: boolean;
  secondary?: boolean;
  preArrow?: boolean;
  arrowLeft?: boolean;
  noArrow?: boolean;
  icon?: boolean;
  inline?: boolean;
  color?: string;
  tokenColor?: string;
  lxl?: boolean;
  large?: boolean;
  small?: boolean;
  bold?: boolean;
  black?: boolean;
  submit?: boolean;
  tagged?: boolean;
  id?: string;
  static?: boolean;
  wrapper?: boolean;
  underline?: boolean;
  title?: string;
  noHover?: boolean;
  arrowType?: string;
  hideFromCrawl?: boolean;
  roleButton?: boolean;
  ariaLabel?: string;
  focusInverse?: boolean;
};

const { t } = useI18n();
const emit = defineEmits<LinkEmits>();
const props = withDefaults(defineProps<LinkProps>(), {
  href: undefined,
  to: undefined,
  target: undefined,
  color: undefined,
  tokenColor: undefined,
  id: undefined,
  title: undefined,
  arrowType: undefined,
  ariaLabel: undefined,
  focusInverse: undefined,
});

const hasArrow = computed(() => !props.icon && !props.inline && !props.noArrow);
const hasPreArrow = computed(() => props.preArrow && unref(hasArrow));
const hasPostArrow = computed(() => !props.preArrow && unref(hasArrow));
const hrefAttribute = computed(() => (props.hideFromCrawl || !props.href ? '' : 'href'));

const linkClasses = computed(() => ({
  'link--secondary': props.secondary,
  'link--disabled': props.disabled,
  'link--inline': props.inline && !props.icon,
  'link--icon': props.icon && !props.inline,
  'link--inline-icon': props.inline && props.icon,
  'link--lxl': props.lxl,
  'link--large': props.large,
  'link--small': props.small,
  'link--bold': props.bold,
  'link--black': props.black,
  'link--tagged': props.tagged,
  'link--wrapper': props.wrapper,
  'link--underline': props.underline,
  'link--no-hover': props.noHover,
  'link--flex': props.arrowType,
  'link--pre-arrow': unref(hasPreArrow),
  'link--post-arrow': unref(hasPostArrow) && !props.arrowType,
  'link--arrow-left': props.arrowLeft,
  [`link--color-${props.color}`]: props.color,
  'link--focus-inverse': props.focusInverse,
}));

const { linkTag, linkTo, linkHref, linkButtonType, linkRoleType } = useLinks(props, 'button');
const { handleClick, handleMiddleClick } = useHideFromCrawl(props, emit);
// This part was added since keydown.enter also triggered the handleClick function
const attrs = useAttrs();
defineOptions({
  inheritAttrs: true,
});

const onKeydownEnter = (event: any) => {
  const handler = attrs.onKeydown;
  if (typeof handler === 'function') {
    handler(event);
  } else {
    handleClick(event);
  }
};
</script>

<style lang="scss" scoped>
.link {
  display: inline;
  border: 0;
  padding: 0;
  font-size: map-get($font-sizes, 'm');
  font-family: $font-primary;
  background-color: transparent;
  color: palette(grey, dark);
  text-decoration: none;
  text-align: left;
  cursor: pointer;
  line-height: 1.5;
  appearance: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  &:focus {
    outline: none;
  }
  &:focus-visible {
    @include focus-outline-default;
    outline-offset: 2px;
  }

  .link-text {
    display: inline;

    &.link-text--pre {
      padding-left: 0.3em;
    }

    &.link-text--post {
      padding-right: 0.3em;
    }
  }

  &:hover {
    color: $color-brand-fnmz-green;

    .link-text {
      text-decoration: underline;
    }
  }

  &.link--icon {
    font-weight: bold;

    &:deep(.icon) {
      display: block;
      margin: math.div($pad, 2) 0;
      line-height: 1;
      text-align: center;
    }

    .link-text {
      font-weight: map-get($font-weights, 'normal');
      text-decoration: none;

      &:hover {
        text-decoration: none;
      }
    }
  }

  &.link--inline-icon {
    font-size: map-get($font-sizes, 'm');

    .link-text {
      line-height: 22px;

      &:hover {
        text-decoration: underline;
      }
    }

    :deep(.icon) {
      display: inline-block;
      vertical-align: top;
      margin: 0 6px 0 0;
    }
  }

  &.link--inline {
    font-size: inherit;

    .link-text {
      text-decoration: underline;
    }

    &:hover {
      .link-text {
        text-decoration: none;
      }
    }
  }

  &.link--focus-inverse {
    &:focus-visible {
      @include focus-outline-inverse;
      outline-offset: -2px;
    }
  }

  &.link--secondary {
    color: palette(grey, base);
  }

  &.link--lxl {
    font-size: map-get($font-sizes, 'lxl');
  }

  &.link--large {
    font-size: map-get($font-sizes, 'l');
  }

  &.link--small {
    font-size: map-get($font-sizes, 's');
  }

  &.link--bold {
    font-weight: map-get($font-weights, 'bold');
  }

  &.link--black {
    font-weight: map-get($font-weights, 'black');
  }

  &.link--tagged {
    &:hover {
      .link-text {
        text-decoration: none;

        &:deep(.link-text-inner) {
          text-decoration: underline;
        }
      }
    }
  }

  &.link--underline {
    .link-text {
      text-decoration: underline;
    }
  }

  &.link--disabled,
  &:disabled,
  &:disabled[disabled] {
    cursor: default;
    pointer-events: none;
    opacity: 0.5;
  }

  &.link--wrapper {
    display: block;
    text-decoration: none;
    margin-bottom: $pad;

    .link-text {
      text-decoration: none;
      padding: 0;
    }

    &.link--inline {
      display: inline;
      margin: 0;
    }
  }

  &.link--no-hover {
    &:hover {
      text-decoration: none;

      .link-text {
        text-decoration: none;
        color: palette(grey, dark);
      }
    }
  }

  &.link--flex {
    display: flex;
    gap: 0.625 * $pad;
  }

  &.link--pre-arrow {
    &:before {
      display: inline;
      position: static;
      content: '\203A\200B';
    }

    &.link--arrow-left {
      &:before {
        content: '\2039\200B';
      }
    }
  }

  &.link--post-arrow {
    &:after {
      display: inline;
      position: static;
      content: '\200B\203A';
    }

    &.link--arrow-left {
      &:after {
        content: '\200B\2039';
      }
    }
  }

  @each $palette, $tones in $palettes {
    @each $tone, $hex in $tones {
      &.link--color-#{$palette}#{if($tone != 'base', '-#{$tone}', '')} {
        color: $hex;
      }
    }
  }
}
</style>
